import {
  Checkbox,
  DefaultModalDialog,
  DurationViewer,
  Heading,
  Input,
  InputField,
  Link,
  Text,
} from '~/common/components/defaultUIComponents';
import {
  BiCloudDownload,
  BiImageAlt,
  BiCopy,
  BiEdit,
  BiListUl,
  BiTrash,
} from 'react-icons/bi';
import { Box, Flex, HStack, Tag, Tooltip, Spinner } from '@chakra-ui/react';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { Project } from '~/common/entities/Project/ProjectTypes';
import { useChecklistPdfDownloader } from '../Project/utils';
import { useCopyProject, useDeleteProject } from './utils';
import ProjectPicturePopup from './ProjectPicturePopup';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import ScrollableOptions from './ScrollableOptions';
import { Paper } from '~/common/components/Paper';
import { IconContext } from 'react-icons/lib';
import * as Router from 'react-router-dom';
import React, { useState } from 'react';
import _ from 'lodash';

interface ProjectListItemProps {
  project: Project;
}

export const ProjectListItem: React.FC<ProjectListItemProps> = ({
  project,
}) => {
  const [isProjectPictureOpen, setIsProjectPictureOpen] = useState(false);
  const navigate = Router.useNavigate();
  const translate = useTranslate();
  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery();
  const { mutate: toggleIsOption } = ProjectApi.hooks.useToggleOptionMutation();
  const { confirmOpen, setConfirmOpen, deleteProject } =
    useDeleteProject(project);
  const { copyOpen, setCopyOpen, copyProject } = useCopyProject(project);
  const [projectToDeleteInput, setProjectToDeleteInput] = React.useState('');
  const containingProjects = projects?.filter((p) =>
    p.option_ids?.includes(project.id),
  );
  const { handleGetChecklist, isChecklistPdfLoading } =
    useChecklistPdfDownloader(project!);

  return (
    <>
      <DefaultModalDialog
        dialogHeaderText="projectTile.duplicateProject.dialogHeaderText"
        submitText="projectTile.duplicateProject.submitText"
        isOpen={copyOpen}
        onCancel={() => setCopyOpen(false)}
        onSubmit={copyProject}
        showCancelButton
        size="xl"
        initialValues={{ newName: '' }}
      >
        <InputField
          name="newName"
          label="projectTile.duplicateProject.input.newName"
          placeholder=""
        />
      </DefaultModalDialog>

      <DefaultModalDialog
        dialogHeaderText="projectTile.deleteProject.dialogHeaderText"
        submitText="projectTile.deleteProject.submitText"
        isOpen={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onSubmit={() => deleteProject()}
        showCancelButton
        size="lg"
        submitButtonDangerVariant
        disableSubmitButton={projectToDeleteInput !== project.name}
        initialValues={{}}
      >
        <Input
          label="projectTile.deleteProject.confirmDeleting"
          labelParams={[project.name]}
          placeholder=""
          onChange={(value) =>
            setProjectToDeleteInput(value.target.value.trim())
          }
        />
        <Text mt="1.5" fontWeight="bold" color="red.500">
          projectTile.deleteProject.warning
        </Text>
      </DefaultModalDialog>
      <Paper
        transitionDuration="1000"
        _hover={{ shadow: 'xl', w: 'full', g: 'full' }}
        w="full"
        h="full"
      >
        <Flex justify="space-between" alignItems="center">
          <Heading fontSize="large" noOfLines={1} width="100%">
            {project.name}
          </Heading>
          <Flex gap={3} justify="flex-end">
            <IconContext.Provider value={{ size: '20px' }}>
              <Tooltip
                label={translate('project.jobs.button.showPDF.text')}
                aria-label={translate('project.jobs.button.showPDF.text')}
              >
                <Box
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (!isChecklistPdfLoading) {
                      void handleGetChecklist();
                    }
                  }}
                >
                  {isChecklistPdfLoading ? (
                    <Spinner boxSize="20px" color="gray.600" thickness="2px" />
                  ) : (
                    <BiCloudDownload />
                  )}
                </Box>
              </Tooltip>
              <Tooltip
                label={translate('project.jobs.button.showProjectpic')}
                aria-label={translate('project.jobs.button.showProjectpic')}
              >
                <Box
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => setIsProjectPictureOpen(true)}
                >
                  <BiImageAlt />
                </Box>
              </Tooltip>
              <Tooltip
                label={translate('project.jobs')}
                aria-label={translate('project.jobs')}
              >
                <Box
                  cursor="pointer"
                  onClick={() =>
                    navigate(routes.PROJECT.replace(':id', String(project.id)))
                  }
                >
                  <BiListUl />
                </Box>
              </Tooltip>
              <Tooltip
                label={translate('projectTile.editProject.tooltip.label')}
                aria-label={translate('projectTile.editProject.tooltip.label')}
              >
                <Box
                  _hover={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(
                      routes.EDIT_PROJECT.replace(':id', String(project.id)),
                    )
                  }
                >
                  <BiEdit />
                </Box>
              </Tooltip>
              <Tooltip
                label={translate('projectTile.duplicateProject.tooltip.label')}
                aria-label={translate(
                  'projectTile.duplicateProject.tooltip.label',
                )}
              >
                <Box
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => setCopyOpen(true)}
                >
                  <BiCopy />
                </Box>
              </Tooltip>
              <Tooltip
                label={translate('projectTile.deleteProject.tooltip.label')}
                aria-label={translate(
                  'projectTile.deleteProject.tooltip.label',
                )}
              >
                <Box
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => setConfirmOpen(true)}
                >
                  <BiTrash color="#E53E3E" />
                </Box>
              </Tooltip>
            </IconContext.Provider>
          </Flex>
        </Flex>
        {!project.option_ids?.length && (
          <Checkbox
            isChecked={project.is_option}
            onChange={() => toggleIsOption(project)}
          >
            project.newProject.checkbox.makeOption
          </Checkbox>
        )}
        <DurationViewer
          durationInSeconds={project.project_time ?? 0}
          label="projectTile.timeOfProject.label"
          labelDescription="projectTile.timeOfProject.labelDescription"
          spaceBetween
        />
        <DurationViewer
          durationInSeconds={project.calculated_project_time ?? 0}
          label="projectTile.caluculatedTimeOfProject.label"
          labelDescription="projectTile.caluculatedTimeOfProject.labelDescription"
          spaceBetween
        />
        <ScrollableOptions project={project} projects={projects!} />
        {containingProjects && containingProjects.length > 0 && (
          <>
            <HStack mt="0.8" spacing={1} overflow="hidden">
              <Text fontWeight="semibold">
                project.projects.containing.options
              </Text>
              <HStack spacing={1}>
                {_.sortBy(containingProjects, ['name'], 'asc').map(
                  (p, index) => (
                    <HStack key={p.id}>
                      <Link href={routes.PROJECT.replace(':id', String(p.id))}>
                        {p.name}
                        {index < containingProjects.length - 1 && (
                          <span>;</span>
                        )}
                      </Link>
                    </HStack>
                  ),
                )}
              </HStack>
            </HStack>
          </>
        )}
        {project.tags && project.tags.length > 0 && (
          <HStack spacing={1} overflow="hidden" justifyItems="center">
            <Text fontWeight="semibold">project.tags</Text>
            <HStack spacing={1}>
              {project.tags.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </HStack>
          </HStack>
        )}
        <ProjectPicturePopup
          projectId={project.id}
          isOpen={isProjectPictureOpen}
          onClose={() => setIsProjectPictureOpen(false)}
        />
      </Paper>
    </>
  );
};
